import api from './axiosInstance';

/* ============================================================
   CLIENTES
============================================================ */
export const searchClientsBasic = async (searchClientDto) => {
  try {
    const response = await api.post(
      `/Clients/SearchClientBasic`,
      searchClientDto
    );
    return response.data;
  } catch (error) {
    console.error(
      'Error al buscar clientes:',
      error.response?.data || error.message
    );
    throw error;
  }
};

export const createBasicClient = async (clientData) => {
  try {
    const response = await api.post(`/Clients/AddBasic`, clientData);
    return response.data;
  } catch (error) {
    console.error(
      'Error creando cliente básico:',
      error.response?.data || error.message
    );
    throw error;
  }
};

/* ============================================================
   LÍNEAS DE NEGOCIO
============================================================ */
export const fetchBusinessLines = async ({
  page = 1,
  pageSize = 100,
  isEnabled = true,
  search = '',
} = {}) => {
  try {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('pageSize', pageSize);
    if (isEnabled !== null) params.append('isEnabled', isEnabled);
    if (search?.trim()) params.append('search', search.trim());

    const response = await api.get(`/BusinessLines/paged?${params}`);

    return (response.data?.data || []).map((item) => ({
      label: item.name,
      value: item.id,
      isEnabled: item.isEnabled,
    }));
  } catch (error) {
    console.error(
      'Error fetching business lines:',
      error.response?.data || error.message
    );
    throw error;
  }
};

/* ============================================================
   SERVICIOS
============================================================ */
export const fetchServicesByBusinessLine = async (businessLineId) => {
  try {
    const response = await api.get(
      `/Services/ByBusinessLine/${businessLineId}`
    );
    return response.data.map((s) => ({
      label: s.name,
      value: s.id,
    }));
  } catch (error) {
    console.error(
      'Error fetching services:',
      error.response?.data || error.message
    );
    throw error;
  }
};

/* ============================================================
   CASOS DE CLIENTE (CORREGIDO)
============================================================ */
export const fetchCasesByClient = async (rut) => {
  try {
    const response = await api.get(`/Cases/GetClientCases/${rut}`);

    return response.data.map((c) => ({
      caseId: c.caseId,
      code: c.code, // ROL real
      title: c.title,
      tribunal: c.tribunal,
      codTribunal: c.codTribunal, // ID tribunal
      demandante: c.demandante,
      lineaNegocio: c.lineaNegocio,

      // Para dropdowns
      label: `${c.code || 'SIN-ROL'} — ${c.title || 'Sin título'}`,
      value: c.caseId,
    }));
  } catch (error) {
    console.error(
      'Error fetching cases:',
      error.response?.data || error.message
    );
    throw error;
  }
};

/* ============================================================
   TAREAS
============================================================ */
export const fetchTasksByService = async (idService) => {
  try {
    const resp = await api.get(`/Task/by-service/${idService}`);
    return resp.data || [];
  } catch (err) {
    console.error('Error obteniendo tareas:', err);
    return [];
  }
};

/* ============================================================
   ABOGADOS
============================================================ */
export const fetchLawyers = async () => {
  try {
    const [lawyersResp, usersResp] = await Promise.all([
      api.get(`/Users/lawyers`),
      api.get(`/Users/All`),
    ]);

    const users = Array.isArray(usersResp?.data) ? usersResp.data : [];
    const usersById = new Map(users.map((u) => [Number(u.id), u]));

    return (lawyersResp?.data || []).map((l) => {
      const user = usersById.get(Number(l.id));
      return {
        label: l.fullName,
        value: l.id,
        email: user?.email || user?.mail || '',
        phone: user?.phone || user?.telephone || '',
        identifier: user?.identifier || user?.rut || '',
      };
    });
  } catch (err) {
    console.error('Error obteniendo abogados:', err);
    return [];
  }
};

/* ============================================================
   FINISH WIZARD — CREA CONTRATO
============================================================ */
export const createContractWizard = async (payload) => {
  try {
    const isFormData = payload instanceof FormData;

    const response = await api.post(
      `/Documents/finish`,
      payload,
      isFormData
        ? { headers: { 'Content-Type': 'multipart/form-data' } }
        : undefined
    );

    return response.data;
  } catch (error) {
    console.error('Error creando contrato:', error);
    throw error;
  }
};

/* ============================================================
   CONTRACTS WIZARD (NUEVO BACKEND /api/contracts)
============================================================ */
export const createContractWizardDraft = async (payload) => {
  const response = await api.post('/contracts/wizard/draft', payload);
  return response.data;
};

export const saveContractWizardStep = async (contractId, payload) => {
  const response = await api.put(
    `/contracts/${contractId}/wizard-step`,
    payload
  );
  return response.data;
};

export const getContractWizardState = async (contractId) => {
  const response = await api.get(`/contracts/${contractId}/wizard-state`);
  return response.data;
};

export const getContractById = async (contractId) => {
  const response = await api.get(`/contracts/${contractId}`);
  return response.data;
};

export const finishContractWizard = async (contractId, payload) => {
  const response = await api.post(
    `/contracts/${contractId}/wizard/finish`,
    payload,
    {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }
  );

  return response.data;
};

export const sendContractToSignature = async (contractId, payload) => {
  const response = await api.post(
    `/contracts/${contractId}/send-to-signature`,
    payload
  );
  return response.data;
};

export const getContracts = async (params = {}) => {
  const response = await api.get('/contracts', { params });
  return response.data;
};

export const getContractsKpis = async (params = {}) => {
  const response = await api.get('/contracts/kpis', { params });
  return response.data;
};

export const exportContracts = async (params = {}) => {
  const response = await api.get('/contracts/export', {
    params,
    responseType: 'blob',
  });
  return response.data;
};

export const getContractCatalogs = async () => {
  const response = await api.get('/contracts/catalogs');
  return response.data;
};

export const getFeeIncreaseRequests = async (params = {}) => {
  const response = await api.get('/contracts/fees/increase-requests', {
    params,
  });
  return response.data;
};

export const approveFeeIncreaseRequest = async (requestId, payload = {}) => {
  const response = await api.post(
    `/contracts/fees/increase-requests/${requestId}/approve`,
    payload
  );
  return response.data;
};

export const rejectFeeIncreaseRequest = async (requestId, payload = {}) => {
  const response = await api.post(
    `/contracts/fees/increase-requests/${requestId}/reject`,
    payload
  );
  return response.data;
};

export const createFeeIncreaseRequest = async (contractId, payload) => {
  const response = await api.post(
    `/contracts/${contractId}/fees/increase-request`,
    payload
  );
  return response.data;
};

export const searchCreditors = async ({ q = '', take = 30 } = {}) => {
  const response = await api.get('/creditors/search', { params: { q, take } });
  return response.data;
};

export const getWizardCreditors = async (contractId) => {
  const response = await api.get(`/contracts/${contractId}/wizard/creditors`);
  return response.data;
};

export const saveWizardCreditors = async (contractId, payload) => {
  const response = await api.put(
    `/contracts/${contractId}/wizard/creditors`,
    payload
  );
  return response.data;
};

/* ============================================================
   LIFECYCLE: terminate, cancel, transition, signed-url
============================================================ */
export const terminateContract = async (contractId, payload) => {
  const response = await api.post(
    `/contracts/${contractId}/terminate`,
    payload
  );
  return response.data;
};

export const cancelContract = async (contractId, payload = {}) => {
  const response = await api.post(
    `/contracts/${contractId}/cancel`,
    payload
  );
  return response.data;
};

export const transitionContract = async (contractId, payload) => {
  const response = await api.post(
    `/contracts/${contractId}/transition`,
    payload
  );
  return response.data;
};

export const getContractSignedUrl = async (contractId) => {
  const response = await api.get(`/contracts/${contractId}/signed-url`);
  return response.data;
};

export const getAllowedTransitions = async (contractId) => {
  const response = await api.get(
    `/contracts/${contractId}/allowed-transitions`
  );
  return response.data;
};

export const getContractTemplates = async () => {
  const response = await api.get('/contracts/templates');
  return response.data;
};

export const getContractTemplateHtml = async (templateName) => {
  const response = await api.get(`/contracts/templates/${encodeURIComponent(templateName)}/html`);
  return response.data?.html || '';
};
